// Field Ops WRITE family on NomOS (SEN-395 / TZ-2): assets, physical tasks, comments, attachments, links, Triage,
// the Inbox and contacts.
// - People's edits apply immediately (owner ruling): every write is a person's direct write on the generic record API
//   (POST/PATCH/DELETE /objects/<type>/records), never a proposal.
// - Records go through the generic Object API. Only bytes and non-record state use dedicated NomOS routes:
//   /blobs (upload, Sentinel's 1.5 MiB photo / 10 MiB document caps and magic-byte checks are enforced server-side),
//   /objects/<type>/records/<id>/content (download), /comments/<id>/resolve, /field-ops/triage/*, /field-ops/inbox/*.
// - Validation Sentinel does in its service layer that the generic record API does not (status per task type,
//   contact fields, comment length) is mirrored here so the UI gets Sentinel's errors.
// - Gaps throw NomosGapError with the reason; nothing returns a made-up default.
import type {
  ActorOut, Asset, AssetCreate, AssetUpdate, AttachmentMeta, CommentRecord, Contact,
  LinkActionResponse, ListPage, Notification, PhysicalTask, PhysicalTaskCreate, TriageAcceptRequest,
  TriageAcceptResponse, TriageItem, TriageItemDetail, TriageMergeResponse,
} from '@/types';
import { AxiosError } from 'axios';
import { getConfig, nomosFetch, scalar, toAxiosError, zulu, type NomosEnvelope } from '../client';
import type { AnyFn, NomosRegistry } from '../registry';
import {
  ASSET_FIELDS, CONTACT_FIELDS, NomosGapError, TASK_FIELDS, getRow, personRecordFor, queryRows, readCells, resolveSid, resolveTarget,
  sentinelIdsOf, toAssets, toTasks,
} from './fieldops';

const err = (status: number, detail: string, code?: string) => toAxiosError(status, code ? { detail, error: { code, message: detail } } : { detail }, '');
const str = (x: unknown): string | null => { const s = scalar(x); return typeof s === 'string' && s !== '' ? s : null; };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const dateOnly = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : v);
function refId(x: unknown): string | null {
  const c = Array.isArray(x) ? x[0] : x;
  return c && typeof c === 'object' && 'target_record_id' in (c as object) ? (c as { target_record_id: string }).target_record_id : null;
}

// ---------- record writes (a person's direct write; 202 = it became a proposal, which a person never should) ----------
interface WrittenRecord { record_id: string; created_at?: string }
async function writeRecord(method: 'POST' | 'PATCH', type: string, rid: string | null, values: Record<string, unknown>): Promise<WrittenRecord> {
  const path = `/objects/${encodeURIComponent(type)}/records${rid ? `/${encodeURIComponent(rid)}` : ''}`;
  const res = await nomosFetch<NomosEnvelope<WrittenRecord & { status?: string }>>(method, path, { values });
  if (!res?.data?.record_id) {
    // An unattended credential's write is queued as a proposal: never what a person's edit should become.
    throw new NomosGapError(`${type} write was queued for approval instead of applied (the NomOS session is not attended)`);
  }
  return res.data;
}
const archiveRecord = (type: string, rid: string) => nomosFetch('DELETE', `/objects/${encodeURIComponent(type)}/records/${encodeURIComponent(rid)}`);

// ---------- lookups (Sentinel ids -> NomOS record ids) ----------
async function siteFor(buildingId: string, onMissing: () => Error): Promise<string> {
  const rid = await resolveSid('site', buildingId);
  if (!rid) throw onMissing();
  const active = scalar((await readCells('site', [rid], ['is_active'])).get(rid)?.is_active);
  if (active === false) throw onMissing();
  return rid;
}
async function byAttr(type: string, attr: string, value: string): Promise<string | undefined> {
  const { rows } = await queryRows(type, [], { filter: { attribute: attr, op: 'eq', value }, limit: 1 });
  return rows[0]?.record_id;
}
async function recordExists(type: string, rid: string): Promise<boolean> {
  try { await nomosFetch('GET', `/objects/${encodeURIComponent(type)}/records/${encodeURIComponent(rid)}`); return true; } catch (e) {
    if ((e as AxiosError).response?.status === 404) return false;
    throw e;
  }
}
/** Sentinel id (sentinel_<x>_id) or, for records created on NomOS, the record id itself. */
async function sidOrRecord(type: string, attr: string, id: string): Promise<string | undefined> {
  const hit = await byAttr(type, attr, id);
  if (hit) return hit;
  return UUID_RE.test(id) && (await recordExists(type, id)) ? id : undefined;
}
// an assignee id (a Person record id, or a login's user id) -> the Person, through its login link
const personFor = personRecordFor;
const categoryFor = (typeId: string) => sidOrRecord('asset_category', 'sentinel_asset_type_id', typeId);
const energySourceFor = (code: string) => byAttr('energy_source', 'code', code);

// =====================================================================================
// Assets (Sentinel asset_service.add_asset / update_asset / delete_asset)
// =====================================================================================
const ASSET_DIRECT: Record<string, string> = {
  name: 'name', manufacturer: 'manufacturer', model: 'model', serial_number: 'serial_number', install_date: 'installed',
  decommission_date: 'decommission_date', expected_lifespan_years: 'expected_lifespan_years', last_maintenance_date: 'last_maintenance_date',
  next_maintenance_due: 'next_maintenance_due', status: 'status', controllable: 'controllable', rated_power_kw: 'rated_power_kw',
  quantity: 'quantity', zone: 'zone', operating_schedule: 'operating_schedule', control_type: 'control_type',
  responsible_party: 'responsible_party', attributes: 'attributes',
};

const DATE_KEYS = ['install_date', 'decommission_date', 'last_maintenance_date', 'next_maintenance_due'];
async function assetValues(data: AssetCreate | AssetUpdate, partial: boolean): Promise<Record<string, unknown>> {
  const d = data as Record<string, unknown>;
  const has = (k: string) => (partial ? k in d && d[k] !== undefined : true);
  // No NomOS home (see the read family's gaps): device link, connector metadata. The meter point link is not an
  // asset cell: it is Meter.physical_asset ("the asset this meter measures"), written by linkMeterPoint below.
  if (d.device_id) throw new NomosGapError('asset device_id: NomOS assets carry no device link');
  if (d.metadata && Object.keys(d.metadata as object).length) throw new NomosGapError('asset metadata: Sentinel vendor_metadata has no NomOS home');
  if (d.seu_category_id) throw new NomosGapError('asset seu_category_id: no Sentinel id join on NomOS seu_category');
  const v: Record<string, unknown> = {};
  for (const [k, slug] of Object.entries(ASSET_DIRECT)) {
    if (!has(k)) continue;
    let val = d[k] ?? null;
    if (val !== null && DATE_KEYS.includes(k)) val = dateOnly(val as string) ?? null;
    v[slug] = val;
  }
  if (has('category_id')) {
    const c = d.category_id as string | null | undefined;
    if (c) {
      const rid = await categoryFor(c);
      if (!rid) throw err(400, `Asset type ${c} not found`);
      v.category = rid;
    } else v.category = null;
  }
  if (has('energy_source')) {
    const e = d.energy_source as string | null | undefined;
    if (e) {
      const rid = await energySourceFor(e);
      if (!rid) throw err(400, `unknown energy_source '${e}'`);
      v.energy_source = rid;
    } else v.energy_source = null;
  }
  return v;
}

// The asset <-> meter link: Sentinel's asset.meter_point_id is NomOS Meter.physical_asset (energy pack, SEN-23: one
// relation, read from the asset as its metering_roles). The form edits ONE meter point: the asset's first linked
// meter (toAssets reports it as meter_point_id). Picking another moves that link; "none" unlinks every meter.
const meterFor = (id: string) => sidOrRecord('meter', 'sentinel_meter_id', id);
async function linkMeterPoint(assetRid: string, siteRid: string | null, meterPointId: string | null, previous: string | null): Promise<void> {
  const linked = (await queryRows('meter', [], { filter: { attribute: 'physical_asset', op: 'eq', value: assetRid }, limit: 1000 })).rows
    .map((m) => m.record_id);
  if (!meterPointId) {
    for (const m of linked) await writeRecord('PATCH', 'meter', m, { physical_asset: null });
    return;
  }
  const mrid = await meterFor(meterPointId);
  if (!mrid) throw err(400, `Meter point ${meterPointId} not found`);
  if (siteRid) {
    const meterSite = refId((await readCells('meter', [mrid], ['site'])).get(mrid)?.site);
    if (meterSite && meterSite !== siteRid) throw err(400, `Meter point ${meterPointId} belongs to another building`);
  }
  if (!linked.includes(mrid)) await writeRecord('PATCH', 'meter', mrid, { physical_asset: assetRid });
  const prev = previous ? await meterFor(previous) : undefined;
  if (prev && prev !== mrid && linked.includes(prev)) await writeRecord('PATCH', 'meter', prev, { physical_asset: null });
}

async function readAsset(rid: string): Promise<Asset> {
  return (await toAssets([await getRow('asset', rid, ASSET_FIELDS)]))[0];
}

async function createAsset(data: AssetCreate): Promise<Asset> {
  const site = await siteFor(data.building_id, () => err(400, `Building ${data.building_id} not found or inactive`));
  const values = await assetValues(data, false);
  // Sentinel's create defaults: operational, not controllable, a person's declared manual asset, consuming.
  values.status = data.status || 'operational';
  values.controllable = !!data.controllable;
  values.attributes = data.attributes ?? {};
  Object.assign(values, { site, role: 'consuming', provenance: 'declared', source: 'manual' });
  for (const k of Object.keys(values)) if (values[k] === null) delete values[k];
  const w = await writeRecord('POST', 'asset', null, values);
  if (data.meter_point_id) await linkMeterPoint(w.record_id, site, data.meter_point_id, null);
  return readAsset(w.record_id);
}

async function consumingAsset(id: string): Promise<string> {
  const rid = await resolveSid('asset', id);
  const role = rid ? str((await readCells('asset', [rid], ['role'])).get(rid)?.role) : null;
  if (!rid || role !== 'consuming') throw err(404, `Asset ${id} not found`, 'asset_not_found');
  return rid;
}

async function updateAsset(id: string, data: AssetUpdate): Promise<Asset> {
  const rid = await consumingAsset(id);
  const values = await assetValues(data, true);
  if (Object.keys(values).length) await writeRecord('PATCH', 'asset', rid, values);
  if ('meter_point_id' in data && data.meter_point_id !== undefined) {
    const before = await readAsset(rid);
    if ((before.meter_point_id ?? null) !== (data.meter_point_id ?? null)) {
      const site = refId((await readCells('asset', [rid], ['site'])).get(rid)?.site);
      await linkMeterPoint(rid, site, data.meter_point_id ?? null, before.meter_point_id ?? null);
    } else return before;
  }
  return readAsset(rid);
}

async function deleteAsset(id: string): Promise<void> {
  const rid = await consumingAsset(id);
  // Sentinel refuses while tasks reference the asset (archive or reassign them first).
  const { total } = await queryRows('task', [], { filter: { attribute: 'asset', op: 'eq', value: rid }, limit: 1 });
  if (total > 0) throw err(409, `Cannot delete the asset with 0 proposals and ${total} tasks. Archive or reassign them first.`, 'asset_in_use');
  // Its meters are unlinked first (meter.physical_asset), then the asset goes (NomOS archives; Sentinel deletes).
  const { rows } = await queryRows('meter', [], { filter: { attribute: 'physical_asset', op: 'eq', value: rid }, limit: 1000 });
  for (const m of rows) await writeRecord('PATCH', 'meter', m.record_id, { physical_asset: null });
  await archiveRecord('asset', rid);
}

// =====================================================================================
// Physical tasks (Sentinel physical_task_service.create_task / update_task)
// =====================================================================================
const FIELD_OPS_TASK_TYPES = ['visit', 'intervention_step', 'data_fix', 'follow_up'];
const LEGACY_TASK_TYPES = ['inspection', 'audit', 'measurement', 'repair', 'maintenance', 'calibration', 'installation', 'replacement',
  'upgrade', 'cleaning', 'adjustment', 'documentation', 'training', 'other'];
const LEGACY_STATUSES = ['pending', 'assigned', 'in_progress', 'completed', 'verified', 'cancelled'];
const statusesFor = (taskType: string | null | undefined): string[] => (taskType === 'visit'
  ? ['backlog', 'planned', 'in_progress', 'done', 'cancelled', 'signed']
  : taskType && FIELD_OPS_TASK_TYPES.includes(taskType) ? ['backlog', 'planned', 'in_progress', 'done', 'cancelled'] : LEGACY_STATUSES);
const defaultStatus = (taskType: string) => (FIELD_OPS_TASK_TYPES.includes(taskType) ? 'backlog' : 'pending');
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

async function readTask(rid: string): Promise<PhysicalTask> {
  const row = await getRow('task', rid, TASK_FIELDS);
  return (await toTasks([row], new Map(row.created_at ? [[rid, row.created_at]] : [])))[0];
}

async function createTask(data: PhysicalTaskCreate): Promise<PhysicalTask> {
  if (!data.title || !data.title.trim() || data.title.length > 255) throw err(422, 'title must be 1-255 characters');
  if (![...FIELD_OPS_TASK_TYPES, ...LEGACY_TASK_TYPES].includes(data.task_type)) throw err(422, `task_type ${JSON.stringify(data.task_type)} is not valid`);
  const allowed = statusesFor(data.task_type);
  if (data.status && !allowed.includes(data.status)) {
    throw err(422, `status '${data.status}' is not valid for task_type '${data.task_type}'; allowed: ${allowed.join(', ')}`);
  }
  if (data.metadata && Object.keys(data.metadata).length) throw new NomosGapError('task metadata: no NomOS home for free-form task metadata');
  const site = await siteFor(data.building_id, () => err(400, `Building ${data.building_id} not found or inactive`));
  const v: Record<string, unknown> = {
    site, name: data.title, task_type: data.task_type, description: data.description ?? null, priority: data.priority ?? 'medium',
    status: data.status || defaultStatus(data.task_type), assigned_to: data.assigned_to ?? null, due_date: dateOnly(data.due_date) ?? null,
    estimated_hours: data.estimated_hours ?? null, estimated_cost_eur: data.estimated_cost ?? null,
  };
  if (data.assignee_user_id) {
    const p = await personFor(data.assignee_user_id);
    if (!p) throw err(400, `User ${data.assignee_user_id} not found`);
    v.assignee = p;
  }
  if (data.proposal_id) {
    const i = await resolveSid('initiative', data.proposal_id);
    if (!i) throw err(400, `Proposal ${data.proposal_id} not found`);
    v.initiative = i;
  }
  if (data.equipment_id) {
    const a = await resolveSid('asset', data.equipment_id);
    if (!a) throw err(400, `Asset ${data.equipment_id} not found`);
    v.asset = a;
  }
  for (const k of Object.keys(v)) if (v[k] === null) delete v[k];
  // NomOS composes a field-ops task's checklist and notifies the assignee on this write (monad/notifications.py).
  const w = await writeRecord('POST', 'task', null, v);
  return readTask(w.record_id);
}

type TaskPatch = Partial<Pick<PhysicalTask, 'status' | 'title' | 'description' | 'assignee_user_id' | 'task_type' | 'priority' | 'due_date'>>;
async function updateTask(id: string, data: TaskPatch): Promise<PhysicalTask> {
  const rid = await resolveSid('task', id);
  if (!rid) throw err(404, `Task ${id} not found`, 'task_not_found');
  const v: Record<string, unknown> = {};
  // Sentinel's PATCH skips fields sent as null (nothing is cleared through it).
  if (data.status != null) {
    // validated against the task's CURRENT task_type, as Sentinel does
    const cur = str((await readCells('task', [rid], ['task_type'])).get(rid)?.task_type);
    const allowed = statusesFor(cur);
    if (!allowed.includes(data.status)) throw err(400, `status '${data.status}' is not valid for task_type '${cur}'; allowed: ${allowed.join(', ')}`);
    v.status = data.status;
  }
  if (data.title != null) v.name = data.title;
  if (data.description != null) v.description = data.description;
  if (data.task_type != null) v.task_type = data.task_type;
  if (data.priority != null) {
    if (!PRIORITIES.includes(data.priority)) throw err(422, `priority '${data.priority}' is not valid`);
    v.priority = data.priority;
  }
  if (data.due_date != null) v.due_date = dateOnly(data.due_date);
  if (data.assignee_user_id != null) {
    const p = await personFor(data.assignee_user_id);
    if (!p) throw err(400, `User ${data.assignee_user_id} not found`);
    v.assignee = p;
  }
  if (Object.keys(v).length) await writeRecord('PATCH', 'task', rid, v);
  return readTask(rid);
}

// =====================================================================================
// Comments (NomOS record comments)
// =====================================================================================
const KIND_OF_TYPE: Record<string, string> = { task: 'task', initiative: 'intervention', site: 'building', asset: 'asset' };
const SID_ATTR: Record<string, string> = { task: 'sentinel_task_id', initiative: 'sentinel_initiative_id', site: 'sentinel_building_id', asset: 'sentinel_asset_id' };
const MAX_COMMENT_CHARS = 10_000; // = CommentCreate.body max_length (monad/plenum/v1/requests.py)

/** NomOS actor ({type,id}) -> Sentinel ActorOut. */
export function actorOut(a: unknown): ActorOut {
  const o = (a && typeof a === 'object' ? a : {}) as { type?: string; id?: string; name?: string };
  const id = String(o.id ?? '');
  if (o.type === 'tenant-member') return o.name ? { kind: 'user', id, name: o.name } : { kind: 'user', id };
  if (o.type === 'agent') return { kind: 'coo', id };
  if (id.startsWith('mail:')) return { kind: 'email', id: id.slice(5) };
  if (id.startsWith('contact:')) return { kind: 'contact', id: id.slice(8) };
  return { kind: 'system', id };
}
const mentionIds = (m: unknown): string[] => (Array.isArray(m) ? m.map((x) => (x && typeof x === 'object' ? String((x as { id?: unknown }).id ?? '') : String(x))) : []);

interface NomosComment { comment_id: string; record_id: string; parent_id?: string | null; body: string; mentions?: unknown[];
  created_at: string; created_by: unknown; resolved_at?: string | null; resolved_by?: unknown; archived_at?: string | null; object?: string }
const toComment = (c: NomosComment, targetKind: string, targetId: string): CommentRecord => ({
  id: c.comment_id, customer_id: '', target_kind: targetKind, target_id: targetId, parent_id: c.parent_id ?? null, body: c.body,
  mentions: mentionIds(c.mentions), source: actorOut(c.created_by).kind === 'email' ? 'email' : 'ui', email_id: null,
  created_by: actorOut(c.created_by), created_at: zulu(c.created_at), resolved_at: zulu(c.resolved_at ?? null),
  resolved_by: c.resolved_by ? actorOut(c.resolved_by) : null, archived_at: zulu(c.archived_at ?? null),
});

async function addComment(data: { target_kind: string; target_id: string; body: string; parent_id?: string | null; mentions?: string[] }): Promise<CommentRecord> {
  const text = (data.body ?? '').trim();
  if (!text) throw err(400, 'a comment needs text', 'invalid_comment');
  if (text.length > MAX_COMMENT_CHARS) throw err(400, `a comment holds at most ${MAX_COMMENT_CHARS} characters`, 'invalid_comment');
  const { type, rid } = await resolveTarget(data.target_kind, data.target_id);
  const res = await nomosFetch<NomosEnvelope<NomosComment>>('POST', `/objects/${type}/records/${rid}/comments`, {
    body: text, parent_id: data.parent_id ?? null, mentions: (data.mentions ?? []).map((id) => ({ kind: 'user', id })) });
  return toComment(res.data, data.target_kind, data.target_id);
}

async function resolveComment(commentId: string): Promise<CommentRecord> {
  const res = await nomosFetch<NomosEnvelope<NomosComment>>('POST', `/comments/${encodeURIComponent(commentId)}/resolve`);
  const type = res.data.object ?? '';
  const sid = SID_ATTR[type] ? str((await readCells(type, [res.data.record_id], [SID_ATTR[type]])).get(res.data.record_id)?.[SID_ATTR[type]]) : null;
  return toComment(res.data, KIND_OF_TYPE[type] ?? type, sid ?? res.data.record_id);
}

// =====================================================================================
// Attachments (bytes in the NomOS blob store; Attachment records reference a task)
// =====================================================================================
async function rawFetch(method: string, path: string, body?: FormData): Promise<Response> {
  const cfg = getConfig();
  const url = `${cfg.baseUrl}/v1/tenants/${encodeURIComponent(cfg.tenant)}${path}`;
  const headers: Record<string, string> = {};
  if (cfg.token) headers.Authorization = `Bearer ${cfg.token}`;
  let res: Response;
  try { res = await fetch(url, { method, headers, body }); } catch (e) { throw new AxiosError((e as Error).message, 'ERR_NETWORK'); }
  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown = undefined;
    if (text) { try { parsed = JSON.parse(text); } catch { parsed = { detail: text }; } }
    const p = (parsed ?? {}) as { detail?: string; code?: string };
    throw toAxiosError(res.status, p.code ? { detail: p.detail, error: { code: p.code, message: p.detail } } : parsed, url);
  }
  return res;
}

async function addAttachment(subjectKind: string, subjectId: string, file: File): Promise<AttachmentMeta> {
  const { type, rid } = await resolveTarget(subjectKind, subjectId);
  if (type !== 'task') throw new NomosGapError(`attachments on '${subjectKind}': NomOS Attachment has only a task reference`);
  const form = new FormData();
  form.append('file', file);
  const up = (await (await rawFetch('POST', '/blobs', form)).json()) as NomosEnvelope<{ storage_key: string; filename: string; content_type: string; size_bytes: number; sha256: string }>;
  const b = up.data;
  const uploadedAt = new Date().toISOString();
  const w = await writeRecord('POST', 'attachment', null, {
    name: b.filename, task: rid, storage_key: b.storage_key, content_type: b.content_type, size_bytes: b.size_bytes, uploaded_at: uploadedAt });
  return {
    id: w.record_id, customer_id: '', subject_kind: subjectKind, subject_id: subjectId, filename: b.filename, mime: b.content_type,
    bytes: b.size_bytes, sha256: b.sha256, created_by: { kind: 'user', id: '' }, created_at: uploadedAt.replace(/\.\d{3}Z$/, 'Z'),
  };
}

/** Triage-item attachments live on the mail row: their ids are `triage:<email_id>:<blob_id>` (see getTriageItem). */
async function downloadAttachment(attachmentId: string): Promise<Blob> {
  const m = /^triage:([0-9a-f-]{36}):([0-9a-f-]{36})$/i.exec(attachmentId);
  const path = m ? `/field-ops/triage/${m[1]}/attachments/${m[2]}` : `/objects/attachment/records/${encodeURIComponent(attachmentId)}/content`;
  return (await rawFetch('GET', path)).blob();
}

// =====================================================================================
// Links: only task.part_of (task -> intervention) has a NomOS home, as Task.initiative (see the read family).
// =====================================================================================
const PART_OF = new Set(['task.part_of']);
const HAS_TASKS = new Set(['intervention.has_tasks', 'task.has_part']); // Sentinel's inverse; the read family names it task.has_part
async function partOfEnds(rel: string, sourceId: string, targetId: string): Promise<{ task: string; init: string }> {
  let taskId: string; let initId: string;
  if (PART_OF.has(rel)) { taskId = sourceId; initId = targetId; } else if (HAS_TASKS.has(rel)) { taskId = targetId; initId = sourceId; } else {
    throw new NomosGapError(`links rel '${rel}': no NomOS home (generic link table is class c; only task.part_of maps to Task.initiative)`);
  }
  const task = await resolveSid('task', taskId);
  if (!task) throw err(404, `task ${taskId} not found`);
  const init = await resolveSid('initiative', initId);
  if (!init) throw err(404, `intervention ${initId} not found`);
  return { task, init };
}

async function createRecordLink(rel: string, sourceId: string, targetId: string): Promise<LinkActionResponse> {
  const { task, init } = await partOfEnds(rel, sourceId, targetId);
  const cur = refId((await readCells('task', [task], ['initiative'])).get(task)?.initiative);
  if (cur === init) return { rel, source_id: sourceId, target_id: targetId, changed: false, dry_run: false };
  if (cur) throw new NomosGapError('task.part_of: the task is already part of another intervention and NomOS Task.initiative holds one');
  await writeRecord('PATCH', 'task', task, { initiative: init });
  return { rel, source_id: sourceId, target_id: targetId, changed: true, dry_run: false };
}

async function deleteRecordLink(rel: string, sourceId: string, targetId: string): Promise<void> {
  const { task, init } = await partOfEnds(rel, sourceId, targetId);
  const cur = refId((await readCells('task', [task], ['initiative'])).get(task)?.initiative);
  if (cur === init) await writeRecord('PATCH', 'task', task, { initiative: null });
}

// =====================================================================================
// Triage (NomOS /field-ops/triage: inbound mail rows)
// =====================================================================================
interface NomosTriageItem {
  email_id: string; reason: string; status: string; from_address: string; subject: string; created_by: unknown; created_at: string;
  resolved_at: string | null; resolved_by: unknown; subject_type?: string | null; subject_record_id?: string | null;
}
const triageStatus = (s: string) => (s === 'accepted' || s === 'merged' ? 'resolved' : s);
const toTriageItem = (t: NomosTriageItem): TriageItem => ({
  id: t.email_id, customer_id: '', email_id: t.email_id, reason: t.reason as TriageItem['reason'], status: triageStatus(t.status) as TriageItem['status'],
  from_address: t.from_address, subject: t.subject, created_by: actorOut(t.created_by), created_at: zulu(t.created_at),
  resolved_at: zulu(t.resolved_at), resolved_by: t.resolved_by ? actorOut(t.resolved_by) : null,
});
const nextCursor = (offset: number, count: number, total: number) =>
  (offset + count < total ? btoa(`o:${offset + count}`).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_') : null);

function eqFilters(filters: string[] | undefined, allowed: Record<string, (v: string) => string>, what: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of filters ?? []) {
    const [field, op, ...rest] = f.split(':');
    const map = allowed[field];
    if (!map) throw new NomosGapError(`${what} filter '${field}' is not supported on NomOS`);
    if (op !== 'eq') throw new NomosGapError(`${what} filter op '${op}' is not supported on NomOS (eq only)`);
    out[field] = map(rest.join(':'));
  }
  return out;
}
const same = (v: string) => v;

async function listTriageItems(params?: { filter?: string[]; limit?: number; offset?: number }): Promise<ListPage<TriageItem>> {
  const q = eqFilters(params?.filter, { status: same, reason: same, from_address: same, subject: same }, 'Triage');
  const limit = params?.limit ?? 100; const offset = params?.offset ?? 0;
  const res = await nomosFetch<NomosEnvelope<NomosTriageItem[]>>('GET', '/field-ops/triage', undefined, { query: { ...q, limit, offset } });
  const total = res.meta?.pagination?.total ?? res.data.length;
  return { items: res.data.map(toTriageItem), total, next_cursor: nextCursor(offset, res.data.length, total) };
}

async function getTriageItem(id: string): Promise<TriageItemDetail> {
  const res = await nomosFetch<NomosEnvelope<NomosTriageItem & { to_addresses: string[]; cc_addresses: string[]; text_body: string; html_body: string;
    attachments: Array<{ blob_id: string; filename: string; content_type: string; size_bytes: number; sha256: string; attachment_record_id?: string | null }> }>>(
    'GET', `/field-ops/triage/${encodeURIComponent(id)}`);
  const d = res.data;
  const item = toTriageItem(d);
  return {
    ...item, to_addresses: d.to_addresses, cc_addresses: d.cc_addresses, text_body: d.text_body, html_body: d.html_body,
    attachments: d.attachments.map((a) => ({
      id: `triage:${d.email_id}:${a.blob_id}`, customer_id: '', subject_kind: 'triage_item', subject_id: d.email_id, filename: a.filename,
      mime: a.content_type, bytes: a.size_bytes, sha256: a.sha256, created_by: { kind: 'email', id: d.email_id }, created_at: item.created_at,
    })),
  };
}

async function dismissTriageItem(id: string): Promise<TriageItem> {
  return toTriageItem((await nomosFetch<NomosEnvelope<NomosTriageItem>>('POST', `/field-ops/triage/${encodeURIComponent(id)}/dismiss`)).data);
}

const INITIATIVE_TYPES = ['LED', 'HVAC', 'closed_hour_load', 'dh_summer_waste', 'smart_store', 'solar', 'insulation', 'other'];
const displayNumber = (d: unknown): number | null => { const s = str(d); return s && /(\d+)$/.test(s) ? Number(/(\d+)$/.exec(s)![1]) : null; };

async function acceptTriageItem(id: string, data: TriageAcceptRequest): Promise<TriageAcceptResponse> {
  const invalid = (detail: string) => err(400, detail, 'triage_action_invalid');
  const site = await siteFor(data.building_id, () => invalid(`Building ${data.building_id} not found or inactive`));
  let object: 'task' | 'initiative'; const values: Record<string, unknown> = { site };
  if (data.title) values.name = data.title.slice(0, 255);
  if (data.description != null) values.description = data.description;
  if (data.kind === 'task') {
    object = 'task';
    const taskType = data.task_type ?? 'follow_up';
    if (![...FIELD_OPS_TASK_TYPES, ...LEGACY_TASK_TYPES].includes(taskType)) throw invalid(`task_type '${taskType}' is not valid`);
    Object.assign(values, { task_type: taskType, status: defaultStatus(taskType), priority: 'medium' });
    if (data.assignee_user_id) {
      const p = await personFor(data.assignee_user_id);
      if (!p) throw invalid(`User ${data.assignee_user_id} not found`);
      values.assignee = p;
    }
  } else {
    object = 'initiative';
    const it = data.initiative_type ?? 'other';
    if (!INITIATIVE_TYPES.includes(it)) throw invalid(`initiative_type '${it}' is not valid`);
    if (data.owner_user_id) throw new NomosGapError('intervention owner_user_id: NomOS initiative has no owner attribute');
    Object.assign(values, { initiative_type: it, status: 'opportunity' });
  }
  const res = await nomosFetch<NomosEnvelope<{ triage_item: NomosTriageItem; created_object: string; created_record_id: string; created_kenning: string }>>(
    'POST', `/field-ops/triage/${encodeURIComponent(id)}/accept`, { object, values });
  const rid = res.data.created_record_id;
  const cells = (await readCells(object, [rid], object === 'task' ? ['name', 'display_id'] : ['name'])).get(rid) ?? {};
  return {
    triage_item: toTriageItem(res.data.triage_item), created_kind: data.kind, created_id: rid,
    created_display_number: object === 'task' ? displayNumber(cells.display_id) : null,  // gap: initiatives carry no display id on NomOS
    created_title: str(cells.name) ?? res.data.created_kenning,
  };
}

async function mergeTriageItem(id: string, targetKind: 'task' | 'intervention', targetId: string): Promise<TriageMergeResponse> {
  if (targetKind !== 'task' && targetKind !== 'intervention') throw err(400, `cannot merge a Triage item into '${targetKind}'`, 'triage_action_invalid');
  const type = targetKind === 'task' ? 'task' : 'initiative';
  const rid = await resolveSid(type, targetId);
  if (!rid) throw err(400, `${targetKind} ${targetId} not found`, 'triage_action_invalid');
  const res = await nomosFetch<NomosEnvelope<{ triage_item: NomosTriageItem; comment_id: string }>>(
    'POST', `/field-ops/triage/${encodeURIComponent(id)}/merge`, { record_id: rid });
  return { triage_item: toTriageItem(res.data.triage_item), comment_id: res.data.comment_id };
}

// =====================================================================================
// Inbox (NomOS /field-ops/inbox: the caller's notifications)
// =====================================================================================
interface NomosNotification {
  notification_id: string; kind: string; subject_type: string; subject_record_id: string; payload: Record<string, unknown>; actor: unknown;
  read_at: string | null; created_at: string; record_title?: string | null; record_display_id?: string | null;
}
async function toNotifications(ns: NomosNotification[]): Promise<Notification[]> {
  const taskIds = ns.filter((n) => n.subject_type === 'task').map((n) => n.subject_record_id);
  const initIds = ns.filter((n) => n.subject_type === 'initiative').map((n) => n.subject_record_id);
  const [tasks, inits] = await Promise.all([
    taskIds.length ? readCells('task', [...new Set(taskIds)], ['sentinel_task_id']) : Promise.resolve(new Map()),
    initIds.length ? sentinelIdsOf('initiative', initIds) : Promise.resolve(new Map<string, string>()),
  ]);
  return ns.map((n) => {
    const sid = n.subject_type === 'task' ? str((tasks as Map<string, Record<string, unknown>>).get(n.subject_record_id)?.sentinel_task_id) : inits.get(n.subject_record_id);
    return {
      id: n.notification_id, kind: n.kind as Notification['kind'], subject_kind: n.subject_type === 'initiative' ? 'intervention' : 'task',
      subject_id: sid ?? n.subject_record_id, subject_type: n.subject_type, subject_record_id: n.subject_record_id,
      record_display_number: displayNumber(n.record_display_id), record_title: n.record_title ?? null,
      payload: n.payload ?? {}, actor: actorOut(n.actor), read_at: zulu(n.read_at), created_at: zulu(n.created_at),
    };
  });
}

async function listInbox(params?: { unread_only?: boolean; filter?: string[]; limit?: number; offset?: number }): Promise<ListPage<Notification>> {
  const q = eqFilters(params?.filter, { kind: same, subject_kind: (v) => (v === 'intervention' ? 'initiative' : v) }, 'Inbox');
  const limit = params?.limit ?? 100; const offset = params?.offset ?? 0;
  const res = await nomosFetch<NomosEnvelope<NomosNotification[]>>('GET', '/field-ops/inbox', undefined, { query: {
    kind: q.kind, subject_type: q.subject_kind, unread_only: params?.unread_only ? 'true' : undefined, limit, offset } });
  const total = res.meta?.pagination?.total ?? res.data.length;
  return { items: await toNotifications(res.data), total, next_cursor: nextCursor(offset, res.data.length, total) };
}

async function markNotificationRead(id: string): Promise<Notification> {
  const res = await nomosFetch<NomosEnvelope<NomosNotification>>('POST', `/field-ops/inbox/${encodeURIComponent(id)}/read`);
  return (await toNotifications([res.data]))[0];
}

async function markAllNotificationsRead(): Promise<{ updated: number }> {
  return (await nomosFetch<NomosEnvelope<{ updated: number }>>('POST', '/field-ops/inbox/mark-all-read')).data;
}

// =====================================================================================
// Contacts (Sentinel contacts.create rules, on the NomOS contact type)
// =====================================================================================
const CONTACT_ROLES = ['store_manager', 'auditor', 'technician', 'contractor', 'staff'];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
async function createContact(data: { name: string; email: string; phone?: string; role: string; company?: string }): Promise<Contact> {
  const invalid = (d: string) => err(400, d, 'invalid_contact');
  const name = (data.name ?? '').trim();
  if (name.length < 1 || name.length > 200) throw invalid('a contact needs a name of at most 200 characters');
  const email = (data.email ?? '').trim().toLowerCase();
  if (email.length > 254 || !EMAIL_RE.test(email)) throw invalid('not an email address');
  if (!CONTACT_ROLES.includes(data.role)) throw invalid(`unknown contact role '${data.role}'`);
  const opt = (v: string | undefined, lo: number, hi: number, what: string) => {
    const c = (v ?? '').trim() || null;
    if (c !== null && (c.length < lo || c.length > hi)) throw invalid(`${what} must be ${lo}-${hi} characters`);
    return c;
  };
  const phone = opt(data.phone, 3, 40, 'phone'); const company = opt(data.company, 1, 200, 'company');
  // Sentinel: unique among the customer's live contacts (NomOS contact.email is not a unique attribute: checked here).
  if (await byAttr('contact', 'email', email)) throw invalid('this customer already has an active contact with that email');
  const values: Record<string, unknown> = { name, email, role: data.role };
  if (phone) values.phone = phone;
  if (company) values.company = company;
  const w = await writeRecord('POST', 'contact', null, values);
  const row = await getRow('contact', w.record_id, CONTACT_FIELDS);
  return {
    id: w.record_id, customer_id: '', name: str(row.v.name) ?? name, email: str(row.v.email) ?? email, phone: str(row.v.phone), role: (str(row.v.role) ?? data.role) as Contact['role'],
    company: str(row.v.company), created_by: { kind: 'user', id: '' }, created_at: zulu(row.created_at ?? w.created_at ?? ''), archived_at: null,
  };
}

export const fieldopsWritesImpl: NomosRegistry = {
  createAsset: createAsset as unknown as AnyFn,
  updateAsset: updateAsset as unknown as AnyFn,
  deleteAsset: deleteAsset as unknown as AnyFn,
  createTask: createTask as unknown as AnyFn,
  updateTask: updateTask as unknown as AnyFn,
  addComment: addComment as unknown as AnyFn,
  resolveComment: resolveComment as unknown as AnyFn,
  addAttachment: addAttachment as unknown as AnyFn,
  downloadAttachment: downloadAttachment as unknown as AnyFn,
  deleteRecordLink: deleteRecordLink as unknown as AnyFn,
  createRecordLink: createRecordLink as unknown as AnyFn,
  listTriageItems: listTriageItems as unknown as AnyFn,
  getTriageItem: getTriageItem as unknown as AnyFn,
  dismissTriageItem: dismissTriageItem as unknown as AnyFn,
  acceptTriageItem: acceptTriageItem as unknown as AnyFn,
  mergeTriageItem: mergeTriageItem as unknown as AnyFn,
  listInbox: listInbox as unknown as AnyFn,
  markNotificationRead: markNotificationRead as unknown as AnyFn,
  markAllNotificationsRead: markAllNotificationsRead as unknown as AnyFn,
  createContact: createContact as unknown as AnyFn,
};
